/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tab, Tabs, Typography } from '@mui/material'
import { endOfDay, startOfDay } from 'date-fns'
import { PageLoader } from 'components/PageLoader'
import { SubjectTabs } from 'components/SubjectsTabs'
import Select from 'components/Select'
import DateTimeInput from 'components/DateTimeInput'
import { PromocodeHistoryDisplayByTypeEnum, SubjectEnumType } from '__generatedTypes__/globalTypes'
import { PlatformSlug } from 'constants/consts'
import { usePlatformsAndSubjects, Props as UsePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { DateOption, DateOptionsEnum, Filters } from '../PromoCodes/Analytics/consts'
import { formatDate, getFilters } from '../PromoCodes/Analytics/utils'
import * as classes from './styles'
import { CARSGamesStatistics } from './components/CARSGamesStatistics'
import { MembershipCancellations } from './components/MembershipCancellations'

export const DATE_OPTIONS: DateOption[] = [
  {
    id: DateOptionsEnum.Today,
    title: 'Today',
    displayBy: PromocodeHistoryDisplayByTypeEnum.DAYS,
  },
  {
    id: DateOptionsEnum.Yesterday,
    title: 'Yesterday',
    displayBy: PromocodeHistoryDisplayByTypeEnum.DAYS,
  },
  {
    id: DateOptionsEnum.ThisMonth,
    title: 'This Month',
    displayBy: PromocodeHistoryDisplayByTypeEnum.DAYS,
  },
  {
    id: DateOptionsEnum.LastMonth,
    title: 'Last Month',
    displayBy: PromocodeHistoryDisplayByTypeEnum.DAYS,
  },
  {
    id: DateOptionsEnum.ThisYear,
    title: 'This Year',
    displayBy: PromocodeHistoryDisplayByTypeEnum.MONTHS,
  },
  {
    id: DateOptionsEnum.LastYear,
    title: 'Last Year',
    displayBy: PromocodeHistoryDisplayByTypeEnum.MONTHS,
  },
  {
    id: DateOptionsEnum.Lifetime,
    title: 'All Time',
    displayBy: PromocodeHistoryDisplayByTypeEnum.MONTHS,
  },
  {
    id: DateOptionsEnum.CustomDateRange,
    title: 'Custom Date Range',
    displayBy: PromocodeHistoryDisplayByTypeEnum.MONTHS,
  },
]

enum StatisticsTab {
  Games = 'games',
  Memberships = 'memberships',
}

const GAME_STATISTICS_TAB = {
  id: StatisticsTab.Games,
  title: 'Games',
}

const MEMBERSHIP_STATISTICS_TAB = {
  id: StatisticsTab.Memberships,
  title: 'Membership Cancellations',
}

export const Statistics = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const tabId = searchParams.get('tab')
  const platformsAndSubjectsProps: UsePlatformsAndSubjects = useMemo(
    () => ({
      initialSelectedSubjectId: subjectId ?? undefined,
      filterSubjects: (subject) => subject.type === SubjectEnumType.Game,
    }),
    [subjectId]
  )
  const { selectedPlatform, subjects, selectedSubject, selectSubject } =
    usePlatformsAndSubjects(platformsAndSubjectsProps)
  const selectedPlatformSlug = selectedPlatform?.slug as PlatformSlug | undefined
  const tabs = useMemo(
    () =>
      selectedPlatformSlug === PlatformSlug.CARSBooster
        ? [GAME_STATISTICS_TAB, MEMBERSHIP_STATISTICS_TAB]
        : [MEMBERSHIP_STATISTICS_TAB],
    [selectedPlatformSlug]
  )
  const selectedTab = useMemo(() => tabs.find(({ id }) => id === tabId) ?? tabs[0], [tabId, tabs])
  const initialDate = DATE_OPTIONS[2]
  const [selectedDate, setSelectedDate] = useState(initialDate)
  const [filters, setFilters] = useState<Filters | undefined>(getFilters(initialDate))
  const startDate = filters?.from ? formatDate(filters.from, false) : undefined
  const endDate = filters?.to ? formatDate(filters.to, true) : undefined
  const cancellationStartDate = filters?.from ? startOfDay(filters.from).toISOString() : undefined
  const cancellationEndDate = filters?.to ? endOfDay(filters.to).toISOString() : undefined

  useEffect(() => {
    if (!selectedSubject || selectedTab.id !== StatisticsTab.Games) {
      return
    }

    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), subjectId: selectedSubject.id }))
  }, [selectedSubject, selectedTab.id])

  useEffect(() => {
    if (!selectedDate) {
      return
    }

    if (selectedDate.id === DateOptionsEnum.Lifetime) {
      setFilters(undefined)
    } else if (selectedDate.id === DateOptionsEnum.CustomDateRange) {
      setFilters((prev) => prev ?? getFilters(initialDate))
    } else {
      setFilters(getFilters(selectedDate))
    }
  }, [selectedDate])

  const gameAnalyticsVariables = useMemo(
    () => ({
      subjectId: selectedSubject?.id || '',
      startDate: startDate,
      endDate: endDate,
    }),
    [selectedSubject?.id, startDate, endDate]
  )

  if (!selectedPlatform || (selectedTab.id === StatisticsTab.Games && !selectedSubject)) {
    return <PageLoader />
  }

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        Statistics
      </Typography>

      <div css={classes.content}>
        <Tabs
          value={selectedTab.id}
          onChange={(_, tabId: StatisticsTab) =>
            setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), tab: tabId }))
          }
          css={classes.statisticsTabs}
          aria-label="Statistics sections"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.title}
              css={[classes.statisticsTab, selectedTab.id === tab.id && classes.activeStatisticsTab]}
            />
          ))}
        </Tabs>

        <div css={classes.filtersContainer}>
          {selectedTab.id === StatisticsTab.Games && (
            <SubjectTabs
              subjects={subjects}
              selectedSubject={selectedSubject}
              selectSubject={selectSubject}
              css={classes.tabs}
            />
          )}
          <div css={classes.filters}>
            <Select<DateOption>
              value={selectedDate}
              onChange={(value) => {
                if (!value) return

                setSelectedDate(value)
              }}
              items={DATE_OPTIONS}
              size="small"
            />

            {filters && (
              <>
                <DateTimeInput
                  value={filters.from ?? null}
                  onChange={(value) => {
                    setFilters({
                      ...filters,
                      from: value || new Date(),
                    })
                  }}
                  placeholder="Date from"
                  pickerType="date"
                  format="MMMM d, yyyy"
                  shouldDisableDate={(date) => !!filters.to && date > filters.to}
                  withConfirmation={false}
                  disabled={selectedDate.id !== DateOptionsEnum.CustomDateRange}
                  size="small"
                />
                <DateTimeInput
                  value={filters.to ?? null}
                  onChange={(value) => {
                    setFilters({
                      ...filters,
                      to: value || new Date(),
                    })
                  }}
                  placeholder="Date to"
                  pickerType="date"
                  format="MMMM d, yyyy"
                  shouldDisableDate={(date) => !!filters.from && date < filters.from}
                  withConfirmation={false}
                  disabled={selectedDate.id !== DateOptionsEnum.CustomDateRange}
                  size="small"
                />
              </>
            )}
          </div>
        </div>

        {selectedTab.id === StatisticsTab.Games && selectedSubject ? (
          <CARSGamesStatistics
            subjectId={selectedSubject.id}
            startDate={startDate}
            endDate={endDate}
            chartDataVariables={gameAnalyticsVariables}
          />
        ) : (
          <MembershipCancellations
            platformId={selectedPlatform.id}
            platformSlug={selectedPlatformSlug}
            startDate={cancellationStartDate}
            endDate={cancellationEndDate}
          />
        )}
      </div>
    </div>
  )
}
